import { Trans, useTranslation } from "react-i18next";
import { Footer, Button, Container, Link } from "../components";
import { getEnvs } from "../utils/env";
import { mediaQueries } from "../utils/theme";
import { getUrlByLocale, URL_NAMES } from "../utils/translation";
import { getWhiteLabel } from "../utils/white-label";
import ConnectionSvg from "./assets/connection.svg?react";
import EasyInstallSvg from "./assets/easy-install.svg?react";
import NoSimSvg from "./assets/no-sim.svg?react";
import PhoneChatSvg from "./assets/phone-chat.svg?react";
import SupportSvg from "./assets/support.svg?react";
import { Header } from "./header";
import type { TransNamespace } from "../types";
import type { FC, SVGProps, ReactNode } from "react";

const { ASSET_BASE_URL } = getEnvs();

type LayoutProps = {
  children?: ReactNode;
  onPurchase?: () => void;
};

const Layout = (props: LayoutProps) => {
  const { children, onPurchase } = props;
  const { t, i18n } = useTranslation<TransNamespace>("esim:layout");
  const whiteLabel = getWhiteLabel();

  return (
    <div aria-label="Layout" className="grid pt-11">
      <Header />

      <section
        aria-label="Purchase eSIM"
        className="relative flex justify-center px-3 py-5 md:py-20"
      >
        <div className="absolute inset-0 -z-10">
          <img
            className="h-full w-full object-cover"
            src={whiteLabel.heroImageUrl || `${ASSET_BASE_URL}/purchase-bg.jpg`}
            srcSet={
              whiteLabel.heroImageUrl
                ? undefined
                : `${ASSET_BASE_URL}/purchase-bg.jpg, ${ASSET_BASE_URL}/purchase-bg@2x.jpg 2x, ${ASSET_BASE_URL}/purchase-bg@3x.jpg 3x`
            }
            alt="Passport plane tickets."
          />
        </div>
        {children}
      </section>

      <section
        aria-label="Benefits"
        className="px-6 py-10 text-center text-sm md:pb-40 md:pt-20 md:text-xl"
      >
        <h2 className="text-xl font-bold md:text-3xl">{t("Benefits")}</h2>

        <div className="mt-9 grid grid-cols-2 items-start gap-y-7 md:hidden">
          {(
            [
              [EasyInstallSvg, t("Easy Installation")],
              [ConnectionSvg, t("Get connected in +190 countries worldwide!")],
              [PhoneChatSvg, t("You're not changing your phone number")],
              [SupportSvg, t("Customer Support 24/7")],
              [NoSimSvg, t("No need for a physical SIM card")],
            ] as const
          ).map(([Svg, content]) => (
            <BenefitItem
              className="last-of-type:col-span-2"
              key={content}
              svg={Svg}
              content={content}
            />
          ))}
        </div>

        <Container className="mt-16 hidden max-w-4xl gap-14 md:grid">
          <div className="grid grid-cols-[1fr_1fr_1fr] items-start gap-5">
            <BenefitItem
              svg={EasyInstallSvg}
              content={t("Easy Installation")}
            />
            <BenefitItem
              svg={ConnectionSvg}
              content={t("Get connected in +190 countries worldwide!")}
            />
            <BenefitItem
              svg={PhoneChatSvg}
              content={t("You're not changing your phone number")}
            />
          </div>
          <div className="grid grid-cols-[1fr_1fr] gap-5">
            <BenefitItem
              svg={SupportSvg}
              content={t("Customer Support 24/7")}
            />
            <BenefitItem
              svg={NoSimSvg}
              content={t("No need for a physical SIM card")}
            />
          </div>
        </Container>
      </section>

      <section
        aria-label="Simple Installation"
        className="relative overflow-hidden bg-slate-100 text-center text-sm md:h-[538px] md:text-left"
      >
        <div className="md:absolute md:inset-0">
          <Container className="md:h-full">
            <div className="grid gap-4 py-10 md:h-full md:w-1/2 md:content-center md:justify-items-start md:gap-12 md:px-8">
              <h2 className="text-xl font-bold md:text-3xl">
                {t("How does it work?")}
              </h2>
              <div className="md:text-xl">
                <ol className="marker:text-primary list-inside list-decimal md:marker:mr-1">
                  <li className="mt-3">
                    <Trans
                      i18nKey="<bold>Choose</bold><br /> a destination and package"
                      components={{
                        bold: <strong />,
                        br: <br className="md:hidden" />,
                      }}
                    />
                  </li>
                  <li className="mt-3">
                    <Trans
                      i18nKey="<bold>Buy</bold><br /> and receive instant confirmation"
                      components={{
                        bold: <strong />,
                        br: <br className="md:hidden" />,
                      }}
                    />
                  </li>
                  <li className="mt-3">
                    <Trans
                      i18nKey="<bold>Activate</bold><br /> by scanning your QR code"
                      components={{
                        bold: <strong />,
                        br: <br className="md:hidden" />,
                      }}
                    />
                  </li>
                </ol>
                <p className="mt-3">
                  {t("Done, you have instant connectivity anywhere!")}
                </p>
              </div>
              <Button
                className="mt-3 py-3 md:px-7 md:text-xl"
                color="secondary"
                onClick={onPurchase}
              >
                {t("Purchase an eSIM")}
              </Button>
              <Link
                color="primary"
                href={getUrlByLocale(URL_NAMES.LEARN_ESIM, i18n.language)}
              >
                {t("Learn how our eSIM works")}
              </Link>
            </div>
          </Container>
        </div>

        <div className="md:absolute md:right-0 md:h-full md:w-1/2">
          <picture>
            <source
              srcSet={`${ASSET_BASE_URL}/simple-install-large.jpg, ${ASSET_BASE_URL}/simple-install-large@2x.jpg 2x, ${ASSET_BASE_URL}/simple-install-large@3x.jpg 3x`}
              media={mediaQueries.mdRaw}
            />
            <source
              srcSet={`${ASSET_BASE_URL}/simple-install-small.jpg, ${ASSET_BASE_URL}/simple-install-small@2x.jpg 2x, ${ASSET_BASE_URL}/simple-install-small@3x.jpg 3x`}
            />
            <img
              className="w-full object-cover md:h-full md:object-right"
              src={`${ASSET_BASE_URL}/simple-install-large.jpg`}
              alt="A hand holding a phone showing an eSIM plan"
            />
          </picture>
        </div>
      </section>
      <Footer />
    </div>
  );
};

const BenefitItem = (props: {
  svg: FC<SVGProps<SVGSVGElement>>;
  content: string;
  className?: string;
}) => {
  const { svg, content: content, className = "" } = props;
  const Svg = svg;
  return (
    <div
      key={content}
      className={`grid justify-items-center gap-4 ${className}`}
    >
      <Svg className="[&_circle]:stroke-primary [&_path]:fill-primary w-[60px] md:w-[74px]" />
      <span>{content}</span>
    </div>
  );
};

export { Layout };
